import clsx from 'clsx'
import moment from 'moment'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'

import { getFlightDetailsWithDedup } from 'api/order'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import GeoContext from 'contexts/geoContext'
import { shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import noop from 'lib/function/noop'
import { isItemCancelled } from 'lib/order/orderUtils'
import { reportClientError } from 'services/errorReportingService'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import BedBankBookingCard from 'tripPlanner/components/Chat/BookingCards/BedBankBookingCard'
import ExperienceBookingCard from 'tripPlanner/components/Chat/BookingCards/ExperienceBookingCard'
import FlightBookingCard from 'tripPlanner/components/Chat/BookingCards/FlightBookingCard'
import HotelBookingCard from 'tripPlanner/components/Chat/BookingCards/HotelBookingCard'
import { useOrdersByMemberId } from 'tripPlanner/hooks/api/order'
import type { OfferTypeForTripItemTypes } from 'tripPlanner/hooks/api/common'
import { useUpdateChatMutation } from 'tripPlanner/hooks/api/chat'
import {
  hasActiveOrderItems,
  isOrderEligibleForChatTrip,
  isOrderSupportedForChatBookingAddition,
} from 'tripPlanner/hooks/orderChatTripEligibility'
import {
  appendContextTag,
  TRIP_PLANNER_ACTION_CONTEXT_LABEL,
} from 'tripPlanner/utils/contextTag'
import { orderHasMultiItemTypes } from 'tripPlanner/utils/order'
import { getFutureFilteredOrdersForChat } from 'tripPlanner/utils/summary'
import { rem } from 'polished'

type SupportedBookingItemType = 'bedbank' | 'hotel' | 'flight' | 'experience'

export type SelectedBooking = {
  order: App.Order
  offers: Array<OfferTypeForTripItemTypes>
  selectionKey: string
  bookingItemId: string
}

type SelectedBookedFlight = {
  orderId: string
  orderFlightItemId: string
}

const EMPTY_BOOKING_ITEM_IDS: Array<string> = []

interface Props {
  onSendMessage: (message: string) => void
  disabledBookingItemIds?: Array<string>
  onBookingItemsAdded?: (bookingItemIds: Array<string>) => void
  isSubmitLocked?: boolean
  chatId?: number
}

const ListOfOrders = styled.ul`
  li {
    display: flex;
    align-items: center;
    gap: ${rem(12)};
    margin-top: ${rem(12)};

    .order-details {
      flex: 1;
    }

    &.isPackage {
      align-items: flex-start;
    }

    &:first-child {
      margin-top: 0;
    }

    &:empty {
      display: none;
    }

    & > div:empty {
      display: none;
    }
  }
`

function formatIsoDate(value?: moment.MomentInput): string | undefined {
  if (!value) {
    return undefined
  }

  const parsedDate = moment(value)
  if (!parsedDate.isValid()) {
    return undefined
  }

  return parsedDate.format(ISO_DATE_FORMAT)
}

function formatAirportLabel(airportName?: string, airportCode?: string): string | undefined {
  if (airportName && airportCode) {
    return `${airportName} (${airportCode})`
  }

  return airportName || airportCode
}

function buildFlightRouteLabel(flights: Array<App.FlightV2>): string | undefined {
  const firstFlight = flights[0]
  const lastFlight = flights[flights.length - 1]

  const fromLabel = firstFlight
    ? formatAirportLabel(
        firstFlight.departureAirportName,
        firstFlight.departureAirport,
      )
    : undefined
  const toLabel = lastFlight
    ? formatAirportLabel(
        lastFlight.arrivalAirportName,
        lastFlight.arrivalAirport,
      )
    : undefined

  if (!fromLabel || !toLabel) {
    return undefined
  }

  return `${fromLabel} -> ${toLabel}`
}

function getLeHotelPropertyName(
  orderItem: App.OrderItem,
  offer: OfferTypeForTripItemTypes | undefined,
): string | undefined {
  if (orderItem.offer.property?.name) {
    return orderItem.offer.property.name
  }

  if (offer?.type !== 'hotel') {
    return undefined
  }

  if ('property' in offer) {
    return offer.property?.name
  }

  return undefined
}

function isFlightOnlyOrderForChat(order: App.Order): boolean {
  return hasActiveOrderItems(order.flightItems) &&
    !hasActiveOrderItems(order.items) &&
    !hasActiveOrderItems(order.bedbankItems) &&
    !hasActiveOrderItems(order.experienceItems)
}

export function buildScopedOrder(
  order: App.Order,
  scopedItems: Partial<
    Pick<App.Order, 'items' | 'bedbankItems' | 'flightItems' | 'experienceItems'>
  >,
): App.Order {
  return {
    ...order,
    items: [],
    bedbankItems: [],
    flightItems: [],
    experienceItems: [],
    tourItems: [],
    cruiseItems: [],
    transferItems: [],
    carHireItems: [],
    giftCardItems: [],
    insuranceItems: [],
    addonItems: [],
    merchandiseItems: [],
    merchandiseShippingItems: [],
    ...scopedItems,
  }
}

function buildSelectionKey(
  bookingType: SupportedBookingItemType,
  orderId: string,
  itemId: string,
): string {
  return `${bookingType}:${orderId}:${itemId}`
}

function updateSelectedBookings(
  previous: Array<SelectedBooking>,
  nextSelection: SelectedBooking,
  checked: boolean,
): Array<SelectedBooking> {
  if (checked) {
    if (
      previous.some(
        (selectedBooking) =>
          selectedBooking.selectionKey === nextSelection.selectionKey,
      )
    ) {
      return previous
    }

    return [...previous, nextSelection]
  }

  return previous.filter(
    (selectedBooking) =>
      selectedBooking.selectionKey !== nextSelection.selectionKey,
  )
}

function getSelectedBookedFlights(
  selectedBookings: Array<SelectedBooking>,
): Array<SelectedBookedFlight> {
  return selectedBookings.flatMap(({ order }) =>
    order.flightItems
      .filter((item) => !isItemCancelled(item))
      .map((orderFlightItem) => ({
        orderId: order.id,
        orderFlightItemId: orderFlightItem.id,
      })),
  )
}

function buildBookedFlightContextTag({
  orderId,
  orderFlightItemId,
}: SelectedBookedFlight): string {
  return `<Context>\norder_id: ${orderId}\norder_flight_item_id: ${orderFlightItemId}\n</Context>`
}

function buildHotelContextSections(
  order: App.Order,
  offers: Array<OfferTypeForTripItemTypes>,
): Array<string> {
  const fromLeHotelItems = order.items
    .filter((item) => !isItemCancelled(item))
    .map((orderItem) => {
      const matchingOffer = offers.find((offer) => offer.id === orderItem.offerId)
      const propertyName =
        getLeHotelPropertyName(orderItem, matchingOffer) ?? orderItem.offer.name
      const checkIn = formatIsoDate(orderItem.reservation?.startDate)
      const checkOut = formatIsoDate(orderItem.reservation?.endDate)

      return [
        'Hotel booking',
        `Order ID: ${order.id}`,
        `Booking item ID: ${orderItem.id}`,
        `Offer ID: ${orderItem.offerId}`,
        `Property: ${propertyName}`,
        checkIn ? `Check-in: ${checkIn}` : undefined,
        checkOut ? `Check-out: ${checkOut}` : undefined,
      ].filter(Boolean).join('\n')
    })

  const fromBedbankItems = order.bedbankItems
    .filter((item) => !isItemCancelled(item))
    .map((bedbankOrderItem) => {
      const propertyName =
        bedbankOrderItem.offer.property?.name ?? bedbankOrderItem.offer.name
      const checkIn = formatIsoDate(bedbankOrderItem.checkIn)
      const checkOut = formatIsoDate(bedbankOrderItem.checkOut)

      return [
        'Hotel booking',
        `Order ID: ${order.id}`,
        `Booking item ID: ${bedbankOrderItem.id}`,
        `Offer ID: ${bedbankOrderItem.offer.id}`,
        `Property: ${propertyName}`,
        checkIn ? `Check-in: ${checkIn}` : undefined,
        checkOut ? `Check-out: ${checkOut}` : undefined,
      ].filter(Boolean).join('\n')
    })

  return [
    ...fromLeHotelItems,
    ...fromBedbankItems,
  ]
}

function buildExperienceContextSections(order: App.Order): Array<string> {
  return order.experienceItems
    .filter((item) => !isItemCancelled(item))
    .map((orderExperienceItem) => {
      const experienceDate = formatIsoDate(orderExperienceItem.date)

      return [
        'Experience booking',
        `Order ID: ${order.id}`,
        `Booking item ID: ${orderExperienceItem.id}`,
        `Offer ID: ${orderExperienceItem.experienceId}`,
        orderExperienceItem.title ? `Experience: ${orderExperienceItem.title}` : undefined,
        orderExperienceItem.redemptionLocation?.name ?
          `Location: ${orderExperienceItem.redemptionLocation.name}` :
          undefined,
        experienceDate ? `Date: ${experienceDate}` : undefined,
        orderExperienceItem.time ? `Time: ${orderExperienceItem.time}` : undefined,
      ].filter(Boolean).join('\n')
    })
}

async function buildFlightContextSection(
  orderId: string,
  orderFlightItem: App.OrderFlightItem,
): Promise<string> {
  let flights: Array<App.FlightV2> = []
  let fareFlightGroups: Array<Array<App.FlightV2>> = []

  try {
    const flightDetails = await getFlightDetailsWithDedup({
      orderId,
      itemId: orderFlightItem.id,
    })
    fareFlightGroups = flightDetails.journeyFares.fares
      .map((fare) => fare.flightGroup.flights)
      .filter((fareFlights) => fareFlights.length > 0)
    flights = fareFlightGroups.flat()
  } catch(error) {
    reportClientError(error, {
      message: 'Failed to load detailed flight context for the trip planner add booking chat modal',
      orderId,
      orderFlightItemId: orderFlightItem.id,
    })
    flights = []
  }

  const firstFlight = flights[0]
  const lastFlight = flights[flights.length - 1]
  const flightNumbers = flights
    .map((flight) => flight.flightNumber)
    .filter(Boolean)
    .join(', ')

  const fromLabel = firstFlight
    ? formatAirportLabel(
        firstFlight.departureAirportName,
        firstFlight.departureAirport,
      )
    : undefined
  const toLabel = lastFlight
    ? formatAirportLabel(
        lastFlight.arrivalAirportName,
        lastFlight.arrivalAirport,
      )
    : undefined
  const departureDate = formatIsoDate(
    firstFlight?.departingDate ?? orderFlightItem.departureDate,
  )
  const arrivalDate = formatIsoDate(
    lastFlight?.arrivalDate ?? orderFlightItem.arrivalDate,
  )
  const flightRouteLabels = fareFlightGroups
    .map((fareFlights) => buildFlightRouteLabel(fareFlights))
    .filter((routeLabel): routeLabel is string => !!routeLabel)
  const shouldUseRouteSummary =
    flightRouteLabels.length > 1 &&
    !!fromLabel &&
    !!toLabel &&
    fromLabel === toLabel
  const routeSummary = shouldUseRouteSummary
    ? flightRouteLabels.join(' | ')
    : undefined

  return [
    'Flight booking',
    `Order ID: ${orderId}`,
    `Booking item ID: ${orderFlightItem.id}`,
    orderFlightItem.reservationId ?
      `Reservation ID: ${orderFlightItem.reservationId}` :
      undefined,
    flightNumbers ? `Flight number: ${flightNumbers}` : undefined,
    routeSummary ? `Trip legs: ${routeSummary}` : undefined,
    !shouldUseRouteSummary && fromLabel ? `From: ${fromLabel}` : undefined,
    !shouldUseRouteSummary && toLabel ? `To: ${toLabel}` : undefined,
    departureDate ? `Departure date: ${departureDate}` : undefined,
    arrivalDate ? `Arrival date: ${arrivalDate}` : undefined,
  ].filter(Boolean).join('\n')
}

export async function buildBookingContextSections(
  selectedBookings: Array<SelectedBooking>,
): Promise<Array<string>> {
  const sections: Array<string> = []

  for (const selectedBooking of selectedBookings) {
    const { order, offers } = selectedBooking

    sections.push(...buildHotelContextSections(order, offers))
    sections.push(...buildExperienceContextSections(order))

    const activeFlightItems = order.flightItems.filter(
      (item) => !isItemCancelled(item),
    )
    const flightSections = await Promise.all(
      activeFlightItems.map((orderFlightItem) =>
        buildFlightContextSection(order.id, orderFlightItem),
      ),
    )
    sections.push(...flightSections)
  }

  return sections
}

function AddBookingModal({
  onSendMessage,
  disabledBookingItemIds = EMPTY_BOOKING_ITEM_IDS,
  onBookingItemsAdded = noop,
  isSubmitLocked = false,
  chatId,
}: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const modalElement = useModalElementContext()

  const { memberId } = useAppSelector(
    (state) => ({
      memberId: state.auth.account.memberId,
    }),
    shallowEqual,
  )

  const closeModal = useCallback(() => {
    modalElement.resolve(undefined)
  }, [modalElement])

  const [selectedBookings, setSelectedBookings] = useState<Array<SelectedBooking>>(
    [],
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { mutateAsync: updateChat } = useUpdateChatMutation()

  const { data: orders, isLoading: isOrdersLoading } = useOrdersByMemberId(
    currentRegionCode,
    memberId,
  )

  const filteredOrders = useMemo(
    () => getFutureFilteredOrdersForChat(orders),
    [orders],
  )

  const supportedOrders = useMemo(
    () =>
      filteredOrders.filter(
        (order) =>
          isOrderEligibleForChatTrip(order) &&
          isOrderSupportedForChatBookingAddition(order),
      ),
    [filteredOrders],
  )

  const disabledBookingItemIdSet = useMemo(
    () => new Set(disabledBookingItemIds),
    [disabledBookingItemIds],
  )

  useEffect(() => {
    setSelectedBookings((previous) =>
      previous.filter(
        (selectedBooking) =>
          !disabledBookingItemIdSet.has(selectedBooking.bookingItemId),
      ),
    )
  }, [disabledBookingItemIdSet])

  const handleOnSelect = useCallback(
    (
      order: App.Order,
      offers: Array<OfferTypeForTripItemTypes>,
      checked: boolean,
      selectionKey: string,
      bookingItemId: string,
    ) => {
      if (disabledBookingItemIdSet.has(bookingItemId)) {
        return
      }

      setSelectedBookings((previous) =>
        updateSelectedBookings(
          previous,
          {
            order,
            offers,
            selectionKey,
            bookingItemId,
          },
          checked,
        ),
      )
    },
    [disabledBookingItemIdSet],
  )

  const selectedOrderIds = selectedBookings.map(
    (selectedBooking) => selectedBooking.order.id,
  )

  const isLoadingInProgress = isOrdersLoading || isSubmitting

  const failedToAddBookingsMessage = intl.formatMessage({
    id: '-UYQZAC-sYzCfGLe3368',
    defaultMessage: 'We couldn’t add those bookings. Please try again.',
    description: 'Error snackbar message shown when the add bookings chat modal fails to send the selected bookings into the trip planner conversation',
  })

  const handlePrimaryActionClick = useCallback(async() => {
    setIsSubmitting(true)

    // Phase tracker keeps the generic snackbar UX but tags errors so
    // Datadog can distinguish updateChat linking failures from context
    // build / message send failures.
    let phase: 'update_chat' | 'build_context' | 'send_message' = 'update_chat'

    try {
      const selectedBookingItemIds = [
        ...new Set(selectedBookings.map(({ bookingItemId }) => bookingItemId)),
      ]

      // Link the selected orders to the chat so the backend can build
      // orderBooking context and set isBooked=true on created items.
      // This must complete before the message is sent.
      if (chatId) {
        const uniqueOrderIds = [...new Set(selectedBookings.map(({ order }) => order.id))]

        if (uniqueOrderIds.length > 0) {
          await updateChat({
            chatId,
            orderIds: uniqueOrderIds,
          })
        }
      }

      phase = 'build_context'

      // `selectedBookings` contains item-scoped cloned orders, so use the original
      // supported order to decide whether this selection is truly flight-only.
      const isFlightOnlySelection =
        selectedBookings.length > 0 &&
        selectedBookings.every(({ order }) => {
          const originalOrder = supportedOrders.find(
            (supportedOrder) => supportedOrder.id === order.id,
          )

          return originalOrder ? isFlightOnlyOrderForChat(originalOrder) : false
        })
      const selectedBookedFlights = getSelectedBookedFlights(selectedBookings)

      if (isFlightOnlySelection && selectedBookedFlights.length > 0) {
        const displayMessage = intl.formatMessage(
          {
            id: 'iKGhYCknbCBBRG6uYS58',
            defaultMessage:
              'Add {count, plural, one {this booked flight} other {these booked flights}} to my trip.',
            description:
              'Visible user chat message sent after selecting one or more existing booked flights from the add booking modal in trip planner chat',
          },
          { count: selectedBookedFlights.length },
        )
        const flightContextMessage = selectedBookedFlights
          .map(buildBookedFlightContextTag)
          .join('\n')

        phase = 'send_message'
        onSendMessage(`${displayMessage}\n${flightContextMessage}`)
        onBookingItemsAdded(selectedBookingItemIds)
        closeModal()
        return
      }

      const bookingContextSections = await buildBookingContextSections(selectedBookings)

      if (bookingContextSections.length > 0) {
        const displayMessage = intl.formatMessage(
          {
            id: 'bcHmlHxq48wI1VV5--Eo',
            defaultMessage:
              'Add {count, plural, one {this booking} other {these bookings}} to my trip',
            description:
              'Visible user chat message sent after selecting bookings from the add booking modal in the trip planner chat composer',
          },
          { count: selectedBookings.length },
        )
        const contextMessage = `Please add the following existing bookings to my trip:\n\n${bookingContextSections.join('\n\n')}`

        phase = 'send_message'
        onSendMessage(
          appendContextTag(
            displayMessage,
            contextMessage,
            TRIP_PLANNER_ACTION_CONTEXT_LABEL,
          ),
        )
        onBookingItemsAdded(selectedBookingItemIds)
      }

      closeModal()
    } catch(error) {
      reportClientError(error, {
        message: 'Failed to add selected bookings from the trip planner add booking chat modal',
        phase,
        orderIds: selectedBookings.map(({ order }) => order.id),
      })
      showSnackbar(failedToAddBookingsMessage, 'critical')
      // Keep the modal open so the traveller can retry.
    } finally {
      setIsSubmitting(false)
    }
  }, [
    chatId,
    closeModal,
    failedToAddBookingsMessage,
    intl,
    onSendMessage,
    onBookingItemsAdded,
    selectedBookings,
    supportedOrders,
    updateChat,
  ])

  const modalTitle = intl.formatMessage({
    id: 'IdzROLFsTNE1vOMWrIqd',
    defaultMessage: 'Add bookings to trip',
    description: 'Title for add bookings to trip modal in the trip planner feature',
  })

  const noEligibleBookingsHeading = intl.formatMessage({
    id: '6MdYdPMUKtpQBQXo3mii',
    defaultMessage: 'Looks like you have no upcoming eligible bookings',
    description: 'Heading shown in the add booking chat modal when the member has no supported upcoming bookings available to add to their trip',
  })

  const noEligibleBookingsDescription = intl.formatMessage({
    id: '9G5WGTpW6SCVsVIKYpZ8',
    defaultMessage: 'Upcoming hotel, flight, and experience bookings will appear here.',
    description: 'Supporting description shown in the add booking chat modal empty state when there are no supported upcoming bookings to display',
  })

  const continueButtonText = intl.formatMessage({
    id: 'w0bZO-QKmPHt1LUqlgcZ',
    defaultMessage: 'Continue',
    description: 'Button text to continue to the next step in the current multi-step process',
  })

  const cancelButtonText = intl.formatMessage({
    id: '0h3WSfyICL6Yx-LmqJ1k',
    defaultMessage: 'Cancel',
    description: 'Button text to cancel the current action and dismiss the dialog or modal',
  })

  return (
    <Modal
      size="S"
      title={modalTitle}
      primaryActionText={continueButtonText}
      secondaryActionText={cancelButtonText}
      isPrimaryActionDisabled={
        selectedBookings.length === 0 || isOrdersLoading || isSubmitting || isSubmitLocked
      }
      onPrimaryActionClick={handlePrimaryActionClick}
      onSecondaryActionClick={closeModal}
      onClose={closeModal}
    >
      {isLoadingInProgress && <LoadingIndicator inline />}
      {!isLoadingInProgress && (
        <ListOfOrders>
          {supportedOrders.map((upcomingOrder: App.Order) => {
            const orderHasMultiItems = orderHasMultiItemTypes(upcomingOrder)

            return (
              <li
                key={upcomingOrder.id}
                className={clsx({
                  isPackage: orderHasMultiItems,
                  selected: selectedOrderIds.includes(upcomingOrder.id),
                })}
              >
                <VerticalSpacer gap={12} className="order-details">
                  {upcomingOrder.bedbankItems
                    .filter((item) => !isItemCancelled(item))
                    .map((bedbankOrderItem) => {
                      const scopedOrder = buildScopedOrder(upcomingOrder, {
                        bedbankItems: [bedbankOrderItem],
                      })
                      const selectionKey = buildSelectionKey(
                        'bedbank',
                        upcomingOrder.id,
                        bedbankOrderItem.id,
                      )
                      const isBookingItemDisabled = disabledBookingItemIdSet.has(
                        bedbankOrderItem.id,
                      )

                      return (
                        <BedBankBookingCard
                          key={selectionKey}
                          order={scopedOrder}
                          bedbankOrderItem={bedbankOrderItem}
                          disabled={isBookingItemDisabled}
                          onSelect={(order, offers, checked) => {
                            handleOnSelect(
                              order,
                              offers,
                              checked,
                              selectionKey,
                              bedbankOrderItem.id,
                            )
                          }}
                        />
                      )
                    })}
                  {upcomingOrder.items
                    .filter((item) => !isItemCancelled(item))
                    .map((orderItem) => {
                      const scopedOrder = buildScopedOrder(upcomingOrder, {
                        items: [orderItem],
                      })
                      const selectionKey = buildSelectionKey(
                        'hotel',
                        upcomingOrder.id,
                        orderItem.id,
                      )
                      const isBookingItemDisabled = disabledBookingItemIdSet.has(
                        orderItem.id,
                      )

                      return (
                        <HotelBookingCard
                          key={selectionKey}
                          order={scopedOrder}
                          orderItem={orderItem}
                          disabled={isBookingItemDisabled}
                          onSelect={(order, offers, checked) => {
                            handleOnSelect(
                              order,
                              offers,
                              checked,
                              selectionKey,
                              orderItem.id,
                            )
                          }}
                        />
                      )
                    })}
                  {upcomingOrder.flightItems
                    .filter((item) => !isItemCancelled(item))
                    .map((orderFlightItem) => {
                      const scopedOrder = buildScopedOrder(upcomingOrder, {
                        flightItems: [orderFlightItem],
                      })
                      const selectionKey = buildSelectionKey(
                        'flight',
                        upcomingOrder.id,
                        orderFlightItem.id,
                      )
                      const isBookingItemDisabled = disabledBookingItemIdSet.has(
                        orderFlightItem.id,
                      )

                      return (
                        <FlightBookingCard
                          key={selectionKey}
                          order={scopedOrder}
                          orderFlightItem={orderFlightItem}
                          disabled={isBookingItemDisabled}
                          onSelect={(order, offers, checked) => {
                            handleOnSelect(
                              order,
                              offers,
                              checked,
                              selectionKey,
                              orderFlightItem.id,
                            )
                          }}
                        />
                      )
                    })}
                  {upcomingOrder.experienceItems
                    .filter((item) => !isItemCancelled(item))
                    .map((experienceItem) => {
                      const scopedOrder = buildScopedOrder(upcomingOrder, {
                        experienceItems: [experienceItem],
                      })
                      const selectionKey = buildSelectionKey(
                        'experience',
                        upcomingOrder.id,
                        experienceItem.id,
                      )
                      const isBookingItemDisabled = disabledBookingItemIdSet.has(
                        experienceItem.id,
                      )

                      return (
                        <ExperienceBookingCard
                          key={selectionKey}
                          experienceItem={experienceItem}
                          order={scopedOrder}
                          disabled={isBookingItemDisabled}
                          onSelect={(order, offers, checked) => {
                            handleOnSelect(
                              order,
                              offers,
                              checked,
                              selectionKey,
                              experienceItem.id,
                            )
                          }}
                        />
                      )
                    })}
                </VerticalSpacer>
              </li>
            )
          })}
        </ListOfOrders>
      )}
      {!isLoadingInProgress && supportedOrders.length === 0 && (
        <>
          <Heading variant="heading6">
            {noEligibleBookingsHeading}
          </Heading>
          <BodyText variant="large">
            {noEligibleBookingsDescription}
          </BodyText>
        </>
      )}
    </Modal>
  )
}

export default AddBookingModal
